import {
    BarChart3,
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    Copy,
    LogIn,
    Phone,
    Plus,
    RefreshCw,
    Star,
    UserPlus,
    Users,
    X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { checkinCustomer, searchCustomers } from '../../../api/customer.api';
import styles from '../../../assets/scss/pages/CustomerList.module.scss';
import Dropdown from '../../../components/ui/Dropdown';
import { PERMISSIONS } from '../../../config/permissions';
import { useAdminHeaderSlot } from '../../../contexts/AdminHeaderContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatPhoneNumber, formatPoints } from '../../../utils/formatters';

const AVATAR_COLORS = ['#0098F6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];

function getAvatarColor(name) {
    if (!name) return AVATAR_COLORS[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name) {
    if (!name) return '?';
    return name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
}

const TYPE_MAP = {
    vip: { label: 'VIP', class: 'vip' },
    new: { label: 'Mới', class: 'new' },
    normal: { label: 'Thường', class: 'normal' },
    premium: { label: 'Premium', class: 'vip' },
    at_risk: { label: 'Rủi ro', class: 'normal' },
};

const TYPE_OPTIONS = [
    { value: '', label: 'Tất cả loại khách' },
    { value: 'vip', label: 'VIP', dot: '#F59E0B' },
    { value: 'new', label: 'Mới', dot: '#22C55E' },
    { value: 'normal', label: 'Thường', dot: '#0098F6' },
];

const SORT_OPTIONS = [
    { value: 'createAt-DESC', label: 'Mới nhất' },
    { value: 'createAt-ASC', label: 'Cũ nhất' },
    { value: 'point-DESC', label: 'Điểm cao → thấp' },
    { value: 'point-ASC', label: 'Điểm thấp → cao' },
];

const PAGE_SIZE = 20;
const DEFAULT_SORT = 'createAt-DESC';

export default function CustomerList() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { hasPermission } = usePermissions();
    const { setSlot } = useAdminHeaderSlot();
    const canCreate = hasPermission(PERMISSIONS.CUSTOMER.CREATE);
    const canViewPhone = hasPermission(PERMISSIONS.CUSTOMER.VIEW_PHONE);
    const canCheckin = hasPermission(PERMISSIONS.CUSTOMER.CHECKIN);

    const keyword = (searchParams.get('search') || '').trim();

    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

    const [typeFilter, setTypeFilter] = useState('');
    const [sort, setSort] = useState(DEFAULT_SORT);

    const [showKpi, setShowKpi] = useState(false);
    const [kpi, setKpi] = useState({ total: 0, vip: 0, new: 0 });

    const [confirmCustomer, setConfirmCustomer] = useState(null);
    const [checkinLoading, setCheckinLoading] = useState(false);
    const [toast, setToast] = useState(null);

    const fetchCustomers = useCallback(
        async (pageNum) => {
            setLoading(true);
            try {
                const [sortBy, sortOrder] = sort.split('-');
                const params = { limit: PAGE_SIZE, page: pageNum, sortBy, sortOrder };
                if (keyword) params.keyword = keyword;
                if (typeFilter) params.type = typeFilter;

                const { data } = await searchCustomers(params);
                let list = data.customers || data.data || [];
                const pag = data.pagination || {};

                if (pageNum === 1 && !keyword && !typeFilter) {
                    const khachLe = list.filter((c) => c.name === 'Khách lẻ');
                    const rest = list.filter((c) => c.name !== 'Khách lẻ');
                    list = [...khachLe, ...rest];
                }

                setCustomers(list);
                setPagination({
                    total: pag.total || list.length,
                    totalPages: pag.totalPages || 1,
                });
            } catch (err) {
                console.error('Error fetching customers:', err);
                setCustomers([]);
            } finally {
                setLoading(false);
            }
        },
        [keyword, typeFilter, sort],
    );

    // Gộp reset-page + fetch vào một effect để tránh double-fetch khi đổi filter lúc page > 1
    const prevFiltersRef = useRef({ keyword, typeFilter, sort });
    useEffect(() => {
        const prev = prevFiltersRef.current;
        const filterChanged = prev.keyword !== keyword || prev.typeFilter !== typeFilter || prev.sort !== sort;
        prevFiltersRef.current = { keyword, typeFilter, sort };

        if (filterChanged && page !== 1) {
            setPage(1); // sẽ retrigger effect này với page=1 ở render kế
            return;
        }
        fetchCustomers(page);
    }, [page, keyword, typeFilter, sort, fetchCustomers]);

    // KPI
    const fetchKpi = useCallback(async () => {
        try {
            const [allRes, vipRes, newRes] = await Promise.all([
                searchCustomers({ limit: 1, page: 1 }),
                searchCustomers({ limit: 1, page: 1, type: 'vip' }),
                searchCustomers({ limit: 1, page: 1, type: 'new' }),
            ]);
            setKpi({
                total: allRes.data.pagination?.total || 0,
                vip: vipRes.data.pagination?.total || 0,
                new: newRes.data.pagination?.total || 0,
            });
        } catch {
            // ignore
        }
    }, []);

    useEffect(() => {
        fetchKpi();
    }, [fetchKpi]);

    const showToast = (type, text) => {
        setToast({ type, text });
        setTimeout(() => setToast(null), 2500);
    };

    const handleCheckin = async () => {
        if (!confirmCustomer || checkinLoading) return;
        setCheckinLoading(true);
        try {
            await checkinCustomer({ phone: confirmCustomer.phone });
            setConfirmCustomer(null);
            showToast('success', 'Check-in khách vào tiệm thành công');
        } catch (err) {
            setConfirmCustomer(null);
            const msg = err.response?.data?.message;
            showToast('error', Array.isArray(msg) ? msg.join(', ') : msg || 'Check-in thất bại');
        } finally {
            setCheckinLoading(false);
        }
    };

    const handleCopyPhone = async (phone) => {
        if (!phone) return;
        try {
            await navigator.clipboard.writeText(phone);
            showToast('success', 'Đã copy số điện thoại');
        } catch {
            showToast('error', 'Copy số điện thoại thất bại');
        }
    };

    const handleResetFilters = () => {
        if (keyword) navigate('/admin/customers', { replace: true });
        setTypeFilter('');
        setSort(DEFAULT_SORT);
    };

    // Header slot
    useEffect(() => {
        setSlot(
            <div className={styles.headerSlot}>
                <div className={styles.hDropdownWrap}>
                    <Dropdown
                        value={typeFilter}
                        placeholder="Loại khách"
                        onChange={setTypeFilter}
                        options={TYPE_OPTIONS}
                    />
                </div>
                <div className={styles.hDropdownWrap}>
                    <Dropdown value={sort} placeholder="Sắp xếp" onChange={setSort} options={SORT_OPTIONS} />
                </div>
                <button className={styles.hBtnGhost} onClick={handleResetFilters} title="Làm mới bộ lọc">
                    <RefreshCw />
                </button>
                <div className={styles.hDivider} />
                <button className={styles.hBtnGhost} onClick={() => setShowKpi((p) => !p)}>
                    <BarChart3 /> Số liệu
                </button>
                {canCreate && (
                    <button className={styles.hBtnPrimary} onClick={() => navigate('/admin/customers/create')}>
                        <Plus /> Thêm khách hàng
                    </button>
                )}
            </div>,
        );
        return () => setSlot(null);
    }, [setSlot, keyword, typeFilter, sort, canCreate, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

    const { total, totalPages } = pagination;

    const renderPagination = () => {
        if (totalPages <= 1) return null;
        const pages = [];
        const start = Math.max(1, page - 2);
        const end = Math.min(totalPages, page + 2);
        if (start > 1) {
            pages.push(1);
            if (start > 2) pages.push('...');
        }
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < totalPages) {
            if (end < totalPages - 1) pages.push('...');
            pages.push(totalPages);
        }
        const from = (page - 1) * PAGE_SIZE + 1;
        const to = Math.min(page * PAGE_SIZE, total);

        return (
            <div className={styles.pagination}>
                <span className={styles.paginationInfo}>
                    Hiển thị {from} - {to} của {total} khách hàng
                </span>
                <div className={styles.paginationControls}>
                    <button className={styles.pageBtn} disabled={page <= 1} onClick={() => setPage(page - 1)}>
                        <ChevronLeft />
                    </button>
                    {pages.map((p, i) =>
                        p === '...' ? (
                            <span key={`dots-${i}`} className={styles.pageDots}>
                                ...
                            </span>
                        ) : (
                            <button
                                key={p}
                                className={`${styles.pageBtn}${p === page ? ` ${styles.pageBtnActive}` : ''}`}
                                onClick={() => setPage(p)}
                            >
                                {p}
                            </button>
                        ),
                    )}
                    <button className={styles.pageBtn} disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                        <ChevronRight />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <>
            <div className={styles.customerPage}>
                {/* KPI */}
                {showKpi && (
                    <div className={styles.kpis}>
                        <div className={styles.kpi}>
                            <div className={styles.kpiContent}>
                                <span className={styles.kpiLabel}>TỔNG KHÁCH HÀNG</span>
                                <span className={styles.kpiValue}>{kpi.total}</span>
                            </div>
                            <div className={`${styles.kpiIcon} ${styles.kpiIconBlue}`}>
                                <Users />
                            </div>
                        </div>
                        <div className={styles.kpi}>
                            <div className={styles.kpiContent}>
                                <span className={styles.kpiLabel}>KHÁCH VIP</span>
                                <span className={styles.kpiValue}>{kpi.vip}</span>
                            </div>
                            <div className={`${styles.kpiIcon} ${styles.kpiIconPurple}`}>
                                <Star />
                            </div>
                        </div>
                        <div className={styles.kpi}>
                            <div className={styles.kpiContent}>
                                <span className={styles.kpiLabel}>KHÁCH MỚI</span>
                                <span className={styles.kpiValue}>{kpi.new}</span>
                            </div>
                            <div className={`${styles.kpiIcon} ${styles.kpiIconGreen}`}>
                                <UserPlus />
                            </div>
                        </div>
                    </div>
                )}

                {/* Table */}
                <div className={styles.tableWrapper}>
                    {loading ? (
                        <div className={styles.loading}>
                            <div className={styles.spinner} />
                            Đang tải danh sách khách hàng...
                        </div>
                    ) : customers.length === 0 ? (
                        <div className={styles.empty}>Không tìm thấy khách hàng nào</div>
                    ) : (
                        <>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>KHÁCH HÀNG</th>
                                        <th>SỐ ĐIỆN THOẠI</th>
                                        <th>LOẠI</th>
                                        <th>ĐIỂM TÍCH LUỸ</th>
                                        <th>SỞ THÍCH/ GHI CHÚ</th>
                                        {canCheckin && <th></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {customers.map((customer) => {
                                        const typeInfo = TYPE_MAP[customer.type] || TYPE_MAP.normal;
                                        return (
                                            <tr
                                                key={customer.id}
                                                className={
                                                    customer.name !== 'Khách lẻ' ? styles.rowClickable : undefined
                                                }
                                                onClick={() =>
                                                    customer.name !== 'Khách lẻ' &&
                                                    navigate(`/admin/customers/${customer.id}`)
                                                }
                                            >
                                                <td>
                                                    <div className={styles.customerCell}>
                                                        <div
                                                            className={styles.avatar}
                                                            style={{ background: getAvatarColor(customer.name) }}
                                                        >
                                                            {getInitials(customer.name)}
                                                        </div>
                                                        <span className={styles.customerName}>{customer.name}</span>
                                                    </div>
                                                </td>
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    <div
                                                        className={`${styles.phoneCell}${canViewPhone && customer.phone ? ` ${styles.phoneCellCopy}` : ''}`}
                                                        onClick={
                                                            canViewPhone && customer.phone
                                                                ? () => handleCopyPhone(customer.phone)
                                                                : undefined
                                                        }
                                                        title={canViewPhone && customer.phone ? 'Bấm để copy' : undefined}
                                                    >
                                                        <Phone size={14} />
                                                        {canViewPhone && customer.phone ? (
                                                            <>
                                                                <span>{formatPhoneNumber(customer.phone)}</span>
                                                                <Copy size={13} className={styles.copyIcon} />
                                                            </>
                                                        ) : customer.phone ? (
                                                            customer.phone.replace(/(\d{3})\d{4}(\d{3})/, '$1****$2')
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span
                                                        className={`${styles.typeBadge} ${styles[`type${typeInfo.class.charAt(0).toUpperCase() + typeInfo.class.slice(1)}`]}`}
                                                    >
                                                        {typeInfo.label}
                                                    </span>
                                                </td>
                                                <td>
                                                    {customer.name !== 'Khách lẻ' ? (
                                                        <div className={styles.pointCell}>
                                                            <Star size={14} />
                                                            <span>{formatPoints(customer.point)}</span>
                                                        </div>
                                                    ) : (
                                                        <span className={styles.note}>-</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className={styles.note}>
                                                        {customer.name === 'Khách lẻ'
                                                            ? 'Dùng cho khách vãng lai'
                                                            : customer.noteLike || '-'}
                                                    </span>
                                                </td>
                                                {canCheckin && (
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        {customer.name !== 'Khách lẻ' && (
                                                            <button
                                                                className={styles.checkinBtn}
                                                                onClick={() => setConfirmCustomer(customer)}
                                                                title="Check-in khách vào tiệm"
                                                            >
                                                                <LogIn size={14} />
                                                                Check-in
                                                            </button>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {renderPagination()}
                        </>
                    )}
                </div>

                {/* Confirm checkin modal */}
                {confirmCustomer && (
                    <div className={styles.modalOverlay} onClick={() => !checkinLoading && setConfirmCustomer(null)}>
                        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                            <div className={styles.modalHeader}>
                                <span className={styles.modalTitle}>Xác nhận Check-in</span>
                                <button
                                    className={styles.modalClose}
                                    onClick={() => !checkinLoading && setConfirmCustomer(null)}
                                >
                                    <X size={18} />
                                </button>
                            </div>
                            <div className={styles.modalBody}>
                                <div className={styles.modalCustomerInfo}>
                                    <div
                                        className={styles.modalAvatar}
                                        style={{ background: getAvatarColor(confirmCustomer.name) }}
                                    >
                                        {getInitials(confirmCustomer.name)}
                                    </div>
                                    <div>
                                        <div className={styles.modalCustomerName}>{confirmCustomer.name}</div>
                                        <div className={styles.modalCustomerPhone}>
                                            {formatPhoneNumber(confirmCustomer.phone)}
                                        </div>
                                    </div>
                                </div>
                                <p className={styles.modalDesc}>Bạn có chắc muốn check-in khách này vào tiệm không?</p>
                            </div>
                            <div className={styles.modalFooter}>
                                <button
                                    className={styles.modalBtnCancel}
                                    onClick={() => setConfirmCustomer(null)}
                                    disabled={checkinLoading}
                                >
                                    Hủy
                                </button>
                                <button
                                    className={styles.modalBtnConfirm}
                                    onClick={handleCheckin}
                                    disabled={checkinLoading}
                                >
                                    <LogIn size={15} />
                                    {checkinLoading ? 'Đang xử lý...' : 'Check-in'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Toast */}
                {toast && (
                    <div
                        className={`${styles.toast} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`}
                    >
                        {toast.type === 'success' ? <CheckCircle size={15} /> : null}
                        {toast.text}
                    </div>
                )}
            </div>
        </>
    );
}
